import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn } from '../common/utils';
import { createSha256Transformer, createTimestampTransformer } from '../common/sql';
import {
  DBDocumentPageSchema,
  DBDocumentIndexEntrySchema,
  DBExtractionRunSchema,
  DBSourceDocumentSchema,
  DocumentPageSchema,
  DocumentIndexEntrySchema,
  ExtractionRunSchema,
  SourceDocumentSchema,
} from './schemas';

export const SourceDocumentCodecConfig: TransformerConfig<
  typeof SourceDocumentSchema,
  typeof DBSourceDocumentSchema
> = {
  name: 'SourceDocument',
  clientSchema: SourceDocumentSchema,
  dbSchema: DBSourceDocumentSchema,
  transformers: [
    createSha256Transformer<typeof SourceDocumentSchema, typeof DBSourceDocumentSchema>('sha256'),
    createSha256Transformer<typeof SourceDocumentSchema, typeof DBSourceDocumentSchema>(
      'contentFingerprint',
      { nullable: true }
    ),
    createTimestampTransformer<typeof SourceDocumentSchema, typeof DBSourceDocumentSchema>(
      'createdAt'
    ),
    createTimestampTransformer<typeof SourceDocumentSchema, typeof DBSourceDocumentSchema>(
      'updatedAt',
      { nullable: true }
    ),
  ],
};

export const DocumentPageCodecConfig: TransformerConfig<
  typeof DocumentPageSchema,
  typeof DBDocumentPageSchema
> = {
  name: 'DocumentPage',
  clientSchema: DocumentPageSchema,
  dbSchema: DBDocumentPageSchema,
  transformers: [
    createTimestampTransformer<typeof DocumentPageSchema, typeof DBDocumentPageSchema>('createdAt'),
  ],
};

export const DocumentIndexEntryCodecConfig: TransformerConfig<
  typeof DocumentIndexEntrySchema,
  typeof DBDocumentIndexEntrySchema
> = {
  name: 'DocumentIndexEntry',
  clientSchema: DocumentIndexEntrySchema,
  dbSchema: DBDocumentIndexEntrySchema,
  transformers: [
    createTimestampTransformer<typeof DocumentIndexEntrySchema, typeof DBDocumentIndexEntrySchema>(
      'createdAt'
    ),
  ],
};

export const ExtractionRunCodecConfig: TransformerConfig<
  typeof ExtractionRunSchema,
  typeof DBExtractionRunSchema
> = {
  name: 'ExtractionRun',
  clientSchema: ExtractionRunSchema,
  dbSchema: DBExtractionRunSchema,
  transformers: [
    createSha256Transformer<typeof ExtractionRunSchema, typeof DBExtractionRunSchema>('configHash'),
    createTimestampTransformer<typeof ExtractionRunSchema, typeof DBExtractionRunSchema>(
      'startedAt',
      { nullable: true }
    ),
    createTimestampTransformer<typeof ExtractionRunSchema, typeof DBExtractionRunSchema>(
      'finishedAt',
      { nullable: true }
    ),
    createTimestampTransformer<typeof ExtractionRunSchema, typeof DBExtractionRunSchema>(
      'createdAt'
    ),
    createTimestampTransformer<typeof ExtractionRunSchema, typeof DBExtractionRunSchema>(
      'updatedAt',
      { nullable: true }
    ),
  ],
};

export const encodeSourceDocumentToDb = createEncodeToDbFn(SourceDocumentCodecConfig);
export const decodeSourceDocumentFromDb = createDecodeFromDbFn(SourceDocumentCodecConfig);
export const encodeDocumentPageToDb = createEncodeToDbFn(DocumentPageCodecConfig);
export const decodeDocumentPageFromDb = createDecodeFromDbFn(DocumentPageCodecConfig);
export const encodeDocumentIndexEntryToDb = createEncodeToDbFn(DocumentIndexEntryCodecConfig);
export const decodeDocumentIndexEntryFromDb = createDecodeFromDbFn(DocumentIndexEntryCodecConfig);
export const encodeExtractionRunToDb = createEncodeToDbFn(ExtractionRunCodecConfig);
export const decodeExtractionRunFromDb = createDecodeFromDbFn(ExtractionRunCodecConfig);
